import * as React from 'react';

import type ScheduleBoardWorkOrderViewModel from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import { moneyNormalizer, formatDecimalNumber, formatNumberToFractionDigits } from '@acceligentllc/shared/utils/formatting';
import { noRevenue } from '@acceligentllc/shared/utils/productionData';
import * as TimeUtils from '@acceligentllc/shared/utils/time';
import { isFirstRevision } from '@acceligentllc/shared/utils/workOrderRevision';

import { AdditionalColors, ColorHex } from '@acceligentllc/shared/enums/color';
import type { NotificationStatusEnum } from '@acceligentllc/shared/enums/notificationStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';
import { WorkRequestStatus } from '@acceligentllc/shared/enums/workRequestStatus';

import * as ColorUtils from 'af-utils/color.util';

import ScheduleBoardView from 'ab-enums/scheduleBoardView.enum';

import { SCREEN_BREAKPOINT_S, SCREEN_BREAKPOINT_M, SCREEN_BREAKPOINT_L, SCREEN_BREAKPOINT_XL } from 'af-constants/values';

import StatusIcon from 'af-components/StatusIcon';
import Tooltip from 'af-components/Tooltip';

import AttachmentsBadge from '../AttachmentsBadge';
import CopyProgressBar from '../CopyProgressBar';
import NightShiftBadge from '../NightShiftBadge';
import PressAndHoldAnchor from '../PressAndHoldAnchor';
import ProductionDataTooltip from './ProductionDataTooltip';

interface OwnProps {
	workOrder: ScheduleBoardWorkOrderViewModel;
	supervisorName: string;
	projectManagerName: string;
	scheduleBoardView: Nullable<ScheduleBoardView>;
	isCalculationsView: boolean;
	notificationStatus: Nullable<NotificationStatusEnum>;
	zoomLevel: number;
	isAllowedToEditWorkOrders: boolean;
	isAllowedToViewProdData: boolean;
	notificationsEnabled?: boolean;
	isDragging?: boolean;
	openOrderInfoModal: () => void;
	setCopiedWorkOrderCode: (code: Nullable<string>) => void;
	hasOnlyNonBillableWork: boolean;
	shouldRoundTimeIntervalsTo1Min: boolean;
}

type Props = OwnProps;

const _renderCanceledLabel = () => {
	return (
		<div className="schedule-board-card-canceled">
			<span>canceled</span>
		</div>
	);
};

const Info: React.FC<Props> = (props) => {
	const {
		isDragging = false,
		zoomLevel,
		setCopiedWorkOrderCode,
		workOrder: { code },
		openOrderInfoModal,
		scheduleBoardView,
		workOrder,
		projectManagerName,
		supervisorName,
		isCalculationsView,
		notificationStatus,
		notificationsEnabled,
		isAllowedToViewProdData,
		hasOnlyNonBillableWork,
		isAllowedToEditWorkOrders,
		shouldRoundTimeIntervalsTo1Min,
	} = props;

	const [isShortCardInfo, setIsShortCardInfo] = React.useState(false);

	const updateWindowDimensions = React.useCallback(() => {
		const isShort = (window.innerWidth < SCREEN_BREAKPOINT_S)
			|| (window.innerWidth < SCREEN_BREAKPOINT_M)
			|| (window.innerWidth < SCREEN_BREAKPOINT_L && zoomLevel < 2)
			|| (window.innerWidth < SCREEN_BREAKPOINT_XL && zoomLevel < 1);

		if (isShort === isShortCardInfo) {
			return;
		}

		setIsShortCardInfo(isShort);
	}, [isShortCardInfo, zoomLevel]);

	// On mount
	React.useEffect(() => {
		updateWindowDimensions();
		window.addEventListener('resize', updateWindowDimensions);

		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		// Needed for when zoom level changes
		updateWindowDimensions();
	}, [props, updateWindowDimensions]);

	const onHoldEnd = React.useCallback(() => {
		if (!code) {
			throw new Error('Code is required');
		}
		setCopiedWorkOrderCode(code);
	}, [code, setCopiedWorkOrderCode]);

	const onRelease = React.useCallback(() => {
		if (!isDragging) {
			setCopiedWorkOrderCode(null);
		}
	}, [isDragging, setCopiedWorkOrderCode]);

	const renderAddress = React.useCallback(() => {
		const { title, customer, address } = workOrder;

		let tooltipTitle = '';
		tooltipTitle += customer ? `${customer}\n` : '';
		tooltipTitle += title ? `${title}\n` : '';
		tooltipTitle += address ?? '';

		return (
			<div className="schedule-board-card-address">
				<div className="schedule-board-card-address-content" title={tooltipTitle}>
					{customer && <><span>{customer}</span><br /></>}
					{title && <><span>{title}</span><br /></>}
					<span>{address}</span>
				</div>
			</div>
		);
	}, [workOrder]);

	const calculateTotalRevenue = React.useCallback((revenue: Nullable<string>, salesMarkup: Nullable<string>) => {
		if (revenue && salesMarkup) {
			return +revenue + +salesMarkup;
		} else if (revenue) {
			return revenue;
		}
		return null;
	}, []);

	const getTotalRevenueDisplayValue = React.useCallback((
		revenue: Nullable<string>,
		salesMarkup: Nullable<string>,
		_hasOnlyNonBillableWork: boolean,
		noRevenueDisplayValue: string = '-'
	) => {
		const totalRevenue = calculateTotalRevenue(revenue, salesMarkup);

		return _hasOnlyNonBillableWork ? noRevenueDisplayValue : moneyNormalizer(totalRevenue);
	}, [calculateTotalRevenue]);

	const renderInfo = React.useCallback((isCopying: boolean, copyFinished: boolean) => {
		const {
			dueDate,
			revenue,
			salesMarkup,
			jobHours,
			shopHours,
			travelHours,
			crewTypeColor,
			customCrewType,
			isInternal,
			isNightShift,
			codeStripped,
			status,
			revision,
			statusBasedRevision,
			workStart,
			workEnd,
			timeToEnd,
			timeToStart,
			shift,
			isPaused,
			projectJobCode,
			isUnion,
			isPrevailingWage,
			jobStatusColor,
			jobStatusName,
			materialCost,
			payrollDifferential,
		} = workOrder;

		const totalHours = (jobHours ?? 0) + (shopHours ?? 0) + (travelHours ?? 0);
		const isDraft: boolean = status === WorkOrderStatus.DRAFT;
		const isWeeklyView: boolean = scheduleBoardView === ScheduleBoardView.WEEKLY_VIEW;
		const isCanceled: boolean = status === WorkOrderStatus.CANCELED;
		const totalHoursDisplayValue = !totalHours ? '-' : `${shouldRoundTimeIntervalsTo1Min ? formatNumberToFractionDigits(totalHours, 3) : formatDecimalNumber(totalHours, 0, 2)} HR`;
		const formattedDueDate = TimeUtils.formatDate(dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY);
		const revenueSubtractions = (process.env.FTD_DISABLE_PR_DIFF !== 'true' ? payrollDifferential ?? 0 : 0) + +(materialCost ?? 0) - +(salesMarkup ?? 0);
		const totalRevenueDisplayValue = getTotalRevenueDisplayValue(
			revenue,
			salesMarkup,
			hasOnlyNonBillableWork,
			noRevenue({ dueDate: formattedDueDate, hasOnlyNonBillableWork, revenueSubtractions })
		);

		const nightShiftWorkStartDateFormatted = TimeUtils.formatDate(dueDate, TimeFormat.SHORT_DATE, TimeFormat.DATE_ONLY);
		const daysToAdd = timeToStart && timeToEnd && timeToStart > timeToEnd ? 1 : 0;
		const nightShiftWorkEndDate = dueDate && TimeUtils.addDays(TimeUtils.parseDate(dueDate, TimeFormat.DATE_ONLY), daysToAdd);
		const nightShiftWorkEndDateFormatted = TimeUtils.formatDate(nightShiftWorkEndDate, TimeFormat.SHORT_DATE);

		let headerBackgroundClassName = '';
		if (!isInternal) {
			headerBackgroundClassName = !crewTypeColor ?
				ColorUtils.getColorBackgroundClass(AdditionalColors.GREY) :
				ColorUtils.getColorBackgroundClass(crewTypeColor);
		}

		const displayProjectCode = !!projectJobCode && process.env.FTD_PROJECT !== 'true';

		return (
			<>
				<div>
					<div className={`schedule-board-card-crew ${headerBackgroundClassName}`}>
						{workOrder.hasAttachments && <AttachmentsBadge />}
						{
							!!jobStatusColor &&
							<div className="schedule-board-card-job-status" >
								<Tooltip
									message={`${jobStatusName}`}
									placement="top-start"
								>
									<div className="schedule-board-card-job-tooltip" style={{ backgroundColor: ColorHex[jobStatusColor] ?? undefined }} />
								</Tooltip>
							</div>
						}
						<div className="schedule-board-card-status">
							<div className="schedule-board-card-status-icon">
								<StatusIcon
									firstRevision={revision ? isFirstRevision(revision) : undefined}
									isPaused={isPaused}
									notificationsEnabled={notificationsEnabled}
									notificationStatus={notificationStatus}
									status={status}
								/>
							</div>
						</div>
						<div className="schedule-board-card-code">
							{isInternal
								? <Tooltip message={customCrewType ?? ''}><span>{customCrewType}</span></Tooltip>
								: <Tooltip message={`${codeStripped}${revision && !isDraft ? ` REV ${revision}${statusBasedRevision ? `.${statusBasedRevision}` : ''}` : ''}`}>
									<span>
										{codeStripped}{revision && !isDraft ? ` REV ${revision}${statusBasedRevision ? `.${statusBasedRevision}` : ''}` : ''}
									</span>
								</Tooltip>
							}
						</div>
						{
							displayProjectCode &&
							<div className="schedule-board-card-project-code">
								{projectJobCode}
							</div>
						}
					</div>
					{
						// If we put ?? it won't how PW if isUnion is false
						// eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
						(isUnion || isPrevailingWage) &&
						<div
							className="schedule-board-card-flags"
						>
							{
								isUnion &&
								<div className="schedule-board-card-flag">U</div>
							}
							{
								isPrevailingWage &&
								<div className="schedule-board-card-flag">PW</div>
							}
						</div>
					}
					{
						!isCalculationsView && isAllowedToViewProdData && !isInternal &&
						<div
							className="schedule-board-card-metrics"
							title={`${getTotalRevenueDisplayValue(revenue, salesMarkup, hasOnlyNonBillableWork)} / ${totalHoursDisplayValue}`}
						>
							<Tooltip message={
								<ProductionDataTooltip
									hasOnlyNonBillableWork={hasOnlyNonBillableWork}
									isAllowedToEditWorkOrders={isAllowedToEditWorkOrders}
									workOrder={workOrder}
								/>
							}
							>
								{totalRevenueDisplayValue} / {totalHoursDisplayValue}
							</Tooltip>
						</div>
					}
					{
						workOrder.status === WorkOrderStatus.LOCKED &&
						<div
							className="schedule-board-card-locked"
						>
							APPROVED (LOCKED)
						</div>
					}
					{
						workOrder.isPaused &&
						<div
							className="schedule-board-card-on-hold"
						>
							ON HOLD
						</div>
					}
					{isCanceled ? _renderCanceledLabel() : renderAddress()}
					<div
						className="schedule-board-card-supervisor"
						title={`${projectManagerName} / ${supervisorName}`}
					>
						{projectManagerName} / {supervisorName}
					</div>
					<div className="schedule-board-card-shop-work">
						{isNightShift && <div className="night-shift-icon-container"><NightShiftBadge /></div>}
						<div className={`shift-item ${isShortCardInfo ? 'centered' : ''}`}>
							{!isShortCardInfo && <label>{shift ?? 'N/A'}</label>}
							{
								!isNightShift
									? <span className="schedule-board-card-shift" title={`${workStart} - ${workEnd}`}>{workStart} - {workEnd}</span>
									: (
										<div className="schedule-board-card-night-shift">
											<span className="schedule-board-card-shift" title={`${nightShiftWorkStartDateFormatted} ${workStart}`}>{nightShiftWorkStartDateFormatted} {workStart}</span>
											<span className="schedule-board-card-shift" title={`${nightShiftWorkEndDateFormatted} ${workEnd}`}>{nightShiftWorkEndDateFormatted} {workEnd}</span>
										</div>
									)
							}
						</div>
					</div>
				</div>
				{isWeeklyView &&
					<CopyProgressBar
						copyFinished={copyFinished}
						isCopying={isCopying}
					/>
				}
			</>
		);
	}, [
		isAllowedToViewProdData,
		isCalculationsView,
		isShortCardInfo,
		notificationStatus,
		notificationsEnabled,
		projectManagerName,
		renderAddress,
		scheduleBoardView,
		supervisorName,
		workOrder,
		isAllowedToEditWorkOrders,
		hasOnlyNonBillableWork,
		getTotalRevenueDisplayValue,
		shouldRoundTimeIntervalsTo1Min,
	]);

	const isWeeklyView = scheduleBoardView === ScheduleBoardView.WEEKLY_VIEW;

	return (
		<PressAndHoldAnchor
			onClick={openOrderInfoModal}	// NOTE: will not open on calculations
			onHoldEnd={onHoldEnd}
			onRelease={onRelease}
			regularAnchor={!isWeeklyView || workOrder.jobStatus === WorkRequestStatus.FINISHED}
		>
			{renderInfo}
		</PressAndHoldAnchor>
	);
};

export default React.memo(Info);
